import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Snackbar,
  Alert
} from "@mui/material";
import { Visibility, Edit, Delete } from "@mui/icons-material";
import { get_admin_users, get_admin_stats, admin_delete_user, admin_update_user } from "../Api service/APIvariables";
import CustomPagination from "./CustomPagination";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({
    total_users: 0,
    total_pro_users: 0,
    total_cards_scanned: 0,
    total_emails_sent: 0
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  
  // Modals state
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmRoleOpen, setConfirmRoleOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editData, setEditData] = useState({ name: '', email: '', company_name: '', plan_id: 1, role: 'user' });
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  const showNotification = (message, severity = 'error') => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleView = (user) => {
    setSelectedUser(user);
    setViewOpen(true);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditData({
      name: user.name || '',
      email: user.email || '',
      company_name: user.company_name || '',
      plan_id: user.plan_id || 1,
      role: user.role || 'user'
    });
    setEditOpen(true);
  };

  const handleEditSave = () => {
    // Check if role was changed
    if (editData.role !== selectedUser.role) {
      setConfirmRoleOpen(true);
    } else {
      proceedEditSave();
    }
  };

  const proceedEditSave = async () => {
    try {
      const user_id = localStorage.getItem("user_id");
      const token = localStorage.getItem("auth_token");
      const formData = new FormData();
      formData.append("user_id", user_id);
      formData.append("target_user_id", selectedUser.id);
      formData.append("name", editData.name);
      formData.append("email", editData.email);
      formData.append("company_name", editData.company_name);
      formData.append("plan_id", editData.plan_id);
      formData.append("role", editData.role);

      const res = await fetch(admin_update_user, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!data.error) {
        setEditOpen(false);
        setConfirmRoleOpen(false);
        showNotification("User updated successfully!", "success");
        fetchData();
      } else {
        showNotification(data.message, "error");
      }
    } catch (error) {
      console.error(error);
      showNotification("An error occurred while updating the user.", "error");
    }
  };

  const handleDelete = (id) => {
    setUserToDelete(id);
    setConfirmDeleteOpen(true);
  };

  const proceedDelete = async () => {
    try {
      const user_id = localStorage.getItem("user_id");
      const token = localStorage.getItem("auth_token");
      const formData = new FormData();
      formData.append("user_id", user_id);
      formData.append("target_user_id", userToDelete);

      const res = await fetch(admin_delete_user, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!data.error) {
        setConfirmDeleteOpen(false);
        showNotification("User deleted successfully!", "success");
        fetchData();
      } else {
        showNotification(data.message, "error");
      }
    } catch (error) {
      console.error(error);
      showNotification("An error occurred while deleting the user.", "error");
    }
  };

  const fetchData = async () => {
    try {
      const user_id = localStorage.getItem("user_id");
      const token = localStorage.getItem("auth_token");

      if (!user_id || !token) return;

      const formData = new FormData();
      formData.append("user_id", user_id);

      // Fetch Stats
      const statsRes = await fetch(get_admin_stats, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const statsData = await statsRes.json();
      if (!statsData.error) {
        setStats(statsData.data);
      }

      // Fetch Users
      const usersRes = await fetch(get_admin_users, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const usersData = await usersRes.json();
      if (!usersData.error) {
        const processed = usersData.data.reverse().map((row, index) => ({
          ...row,
          generatedId: index + 1
        }));
        setUsers(processed);
      }
    } catch (error) {
      console.error("Error fetching admin data:", error);
    }
  };

  useEffect(() => {
    fetchData();
    window.addEventListener("planUpdated", fetchData);
    return () => window.removeEventListener("planUpdated", fetchData);
  }, []);

  return (
    <Box sx={{ padding: 4, width: "100%", maxWidth: "1200px", margin: "0 auto" }}>
      <Typography variant="h5" sx={{ fontWeight: "bold", mb: 4, color: "#453c72" }}>
        Internal Ops Dashboard (Read Only)
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Users</Typography>
              <Typography variant="h4">{stats.total_users}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Pro Users</Typography>
              <Typography variant="h4" sx={{ color: "#00897B" }}>{stats.total_pro_users}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Cards Scanned</Typography>
              <Typography variant="h4">{stats.total_cards_scanned}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Emails Sent (Today)</Typography>
              <Typography variant="h4">{stats.total_emails_sent}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Users Table */}
      <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, color: "#453c72" }}>
        User Roster & Limits
      </Typography>
      <TableContainer component={Paper} elevation={2}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
            <TableRow>
              <TableCell><strong>No.</strong></TableCell>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Company</strong></TableCell>
              <TableCell align="center"><strong>Status</strong></TableCell>
              <TableCell align="center"><strong>Cards (Today)</strong></TableCell>
              <TableCell align="center"><strong>Emails (Today)</strong></TableCell>
              <TableCell align="center"><strong>Action</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => (
              <TableRow key={row.generatedId}>
                <TableCell>{row.generatedId}</TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.email}</TableCell>
                <TableCell>{row.company_name}</TableCell>
                <TableCell align="center">
                  <Box sx={{
                    display: 'inline-block',
                    px: 1.5, py: 0.5,
                    borderRadius: 1,
                    backgroundColor: row.plan_name === 'Pro' ? '#e1bee7' : 
                                     row.plan_name === 'Plus' ? '#bbdefb' : 
                                     row.plan_name === 'Unlimited' ? '#fff9c4' : '#f5f5f5',
                    color: row.plan_name === 'Pro' ? '#6a1b9a' : 
                           row.plan_name === 'Plus' ? '#1565c0' : 
                           row.plan_name === 'Unlimited' ? '#f57f17' : '#616161',
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    letterSpacing: 0.5
                  }}>
                    {row.plan_name ? row.plan_name.toUpperCase() : 'FREE'}
                  </Box>
                </TableCell>
                <TableCell align="center">{row.cards_today}</TableCell>
                <TableCell align="center">{row.emails_today}</TableCell>
                <TableCell align="center">
                  <Tooltip title="View">
                    <IconButton size="small" color="info" onClick={() => handleView(row)}>
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small" color="primary" onClick={() => handleEdit(row)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">No users found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          marginTop: "16px",
          marginRight: "auto",
        }}
      >
        <CustomPagination
          count={users.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            fontSize: "12px",
            width: "auto",
            justifyContent: "space-between",
            marginBottom: 2,
          }}
        />
      </div>
      {/* View User Modal */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>User Details</DialogTitle>
        <DialogContent dividers>
          {selectedUser && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography><strong>ID:</strong> {selectedUser.id}</Typography>
              <Typography><strong>Name:</strong> {selectedUser.name}</Typography>
              <Typography><strong>Email:</strong> {selectedUser.email}</Typography>
              <Typography><strong>Company:</strong> {selectedUser.company_name}</Typography>
              <Typography><strong>Plan:</strong> {selectedUser.plan_name}</Typography>
              <Typography><strong>Role:</strong> {selectedUser.role}</Typography>
              <Typography><strong>Cards Scanned (Today):</strong> {selectedUser.cards_today}</Typography>
              <Typography><strong>Emails Sent (Today):</strong> {selectedUser.emails_today}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Name" fullWidth value={editData.name} onChange={(e) => setEditData({...editData, name: e.target.value})} />
            <TextField label="Email" fullWidth value={editData.email} onChange={(e) => setEditData({...editData, email: e.target.value})} />
            <TextField label="Company Name" fullWidth value={editData.company_name} onChange={(e) => setEditData({...editData, company_name: e.target.value})} />
            <TextField select label="Plan" fullWidth value={editData.plan_id} onChange={(e) => setEditData({...editData, plan_id: e.target.value})}>
              <MenuItem value={1}>Free</MenuItem>
              <MenuItem value={2}>Plus</MenuItem>
              <MenuItem value={3}>Pro</MenuItem>
              <MenuItem value={4}>Unlimited</MenuItem>
            </TextField>
            <TextField select label="Role" fullWidth value={editData.role} onChange={(e) => setEditData({...editData, role: e.target.value})}>
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained" color="primary">Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Role Change Modal */}
      <Dialog open={confirmRoleOpen} onClose={() => setConfirmRoleOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#d32f2f', fontWeight: 'bold' }}>Confirm Role Change</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            You are changing this user's role from <strong>{selectedUser?.role}</strong> to <strong>{editData.role}</strong>. 
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            {editData.role === 'admin' ? 
              "This will grant the user full administrative access to the platform." : 
              "This will revoke the user's administrative access."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRoleOpen(false)}>Cancel</Button>
          <Button onClick={proceedEditSave} variant="contained" color="error">
            Yes, Change Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Modal */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#d32f2f', fontWeight: 'bold' }}>Delete User</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            Are you sure you want to delete this user? 
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 'bold' }}>
            This action cannot be undone. All cards, templates, and usage data will be permanently wiped.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button onClick={proceedDelete} variant="contained" color="error">
            Yes, Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* Global Snackbar Notification */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={4000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
